import { useEffect, useMemo, useState } from 'react'
import type { ReactElement } from 'react'

import { cn } from '../lib/cn'
import { useAuth } from '../lib/auth'
import { useT } from '../lib/i18n'
import { dayProgress, hourLabel, inZone, zoneOffsetLabel } from '../lib/format'
import { useNow } from '../hooks/useNow'
import { useIsMobile, usePrefersReducedMotion } from '../hooks/useMediaQuery'
import { EMPTY_ACTIVITY, connectedProviders, useBestTimesForProviders } from '../hooks/useBestTimes'
import { Select } from '../components/ui/Select'
import { PROVIDER_META } from '@anubis-connector/shared'
import type { Provider } from '@anubis-connector/shared'

const ALL_NETWORKS = 'all' as const
type NetworkFilter = Provider | typeof ALL_NETWORKS

const HOURS = Array.from({ length: 24 }, (_, hour) => hour)

/**
 * `14:00`. The rail is a 24-hour scale in both locales — `en` formats as `en-GB` and Italian
 * has never done anything else — so the clock face is identical and only the words around it
 * are translated.
 */
const hourClock = (hour: number): string => `${hourLabel(hour)}:00`

/**
 * ── The Now Rail ───────────────────────────────────────────────────────────────
 * A 28 px hairline strip under the top bar, on every authenticated screen.
 *
 * Three things, layered in one line:
 *   1. a 24-hour scale in mono micro-type
 *   2. the active brand's audience-activity profile as a density row
 *   3. a brass marker at the real time **in the brand's timezone**, ticking every 30 s
 *
 * It answers "is my audience awake right now" before anyone asks. It is the one memorable
 * element in the product; nothing else competes with it. Under 768 px the hour scale drops
 * and it collapses to the density row plus the marker.
 */
export const NowRail = (): ReactElement => {
  const t = useT()
  const { activeBrand, activeTimezone } = useAuth()
  const isMobile = useIsMobile()
  const reducedMotion = usePrefersReducedMotion()
  const now = useNow(30_000)

  const providers = useMemo(() => connectedProviders(activeBrand), [activeBrand])
  const [filter, setFilter] = useState<NetworkFilter>(ALL_NETWORKS)
  // A provider picked for one brand means nothing once the active brand changes — its
  // connections are a different set entirely, so the picker resets to "All networks" rather
  // than silently keep showing a network this brand may not even have.
  useEffect(() => {
    setFilter(ALL_NETWORKS)
  }, [activeBrand?.blogId])

  const { activityByProvider, combined, status, idle } = useBestTimesForProviders(
    activeBrand?.blogId ?? null,
    providers,
    activeTimezone,
  )
  const activity = filter === ALL_NETWORKS ? combined : activityByProvider.get(filter) ?? EMPTY_ACTIVITY

  const progress = dayProgress(activeTimezone)
  const brandTime = inZone(now, activeTimezone)
  const currentHour = brandTime.hour()
  const clock = brandTime.format('HH:mm')

  const hasProfile = !idle && status === 'success' && activity.grid.length > 0
  const peak = activity.peakHour

  const summary =
    hasProfile && peak !== null
      ? t('shell.nowRail.localTimeWithPeak', {
          clock,
          timezone: activeTimezone,
          hour: hourClock(peak),
        })
      : t('shell.nowRail.localTime', { clock, timezone: activeTimezone })

  // The one sentence that has to survive a 24-cell strip: it names the peak on hover, where
  // the density row alone only shows it.
  const densityTitle =
    hasProfile && peak !== null ? t('shell.nowRail.busiestAround', { hour: hourClock(peak) }) : undefined

  return (
    <div
      role="region"
      aria-label={
        activeBrand
          ? t('common.shell.nowRail', { brandName: activeBrand.name, timezone: activeTimezone })
          : t('shell.nowRail.regionLabel')
      }
      className="relative flex h-7 shrink-0 items-stretch border-b border-chrome-600 bg-chrome-800 select-none"
    >
      <p className="sr-only">{summary}</p>

      <div className="relative min-w-0 flex-1">
        {/* density row — one segment per hour, single-hue, opacity carries intensity */}
        <div className="absolute inset-0 flex" aria-hidden="true" title={densityTitle}>
          {HOURS.map((hour) => {
            const score = activity.hourly[hour] ?? 0
            return (
              <span
                key={hour}
                className="flex-1"
                style={
                  hasProfile
                    ? { backgroundColor: 'var(--brass)', opacity: 0.05 + score * 0.42 }
                    : undefined
                }
              />
            )
          })}
        </div>

        {/* hour scale — dropped under md, where 24 labels cannot breathe */}
        {!isMobile ? (
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            {HOURS.map((hour) => (
              <span key={hour} className="relative min-w-0 flex-1">
                {hour % 3 === 0 ? (
                  <span
                    className={cn(
                      't-mono pl-1.5',
                      hour === currentHour ? 'text-chrome-ink' : 'text-chrome-mute',
                    )}
                  >
                    {hourLabel(hour)}
                  </span>
                ) : null}
              </span>
            ))}
          </div>
        ) : null}

        {/* the marker — real time in the brand's zone */}
        <span
          aria-hidden="true"
          className="absolute top-0 bottom-0 w-0.5 bg-brass"
          style={{
            left: `${(progress * 100).toFixed(3)}%`,
            transition: reducedMotion ? undefined : 'left var(--dur-3) var(--ease-out)',
          }}
        >
          <span className="absolute -top-px left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-brass" />
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-2 border-l border-chrome-600 pr-3 pl-3">
        {providers.length > 0 && !isMobile ? (
          <Select
            tone="chrome"
            selectSize="sm"
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value as NetworkFilter)
            }}
            aria-label={t('shell.nowRail.networkFilter')}
            className="h-auto border-none bg-transparent py-0 pr-6 pl-0 t-mono text-chrome-mute"
          >
            <option value={ALL_NETWORKS}>{t('shell.nowRail.allNetworks')}</option>
            {providers.map((item) => (
              <option key={item} value={item}>
                {PROVIDER_META[item].label}
              </option>
            ))}
          </Select>
        ) : null}
        <span className="t-mono tabular text-brass">{clock}</span>
        {!isMobile ? (
          <span className="t-mono text-chrome-mute" title={activeTimezone}>
            {zoneOffsetLabel(activeTimezone)}
          </span>
        ) : null}
      </div>
    </div>
  )
}
